import React from 'react';
import { Box, Paper, Skeleton, Stack, Divider } from '@mui/material';

/**
 * CanvasSkeleton
 * - Mimics your Canvas: title/desc at top, then a 2-column grid of field "cards"
 * - Keeps height stable with minHeight + predictable card heights
 * - Supports optional "sections" that span both columns
 */
export default function CanvasSkeleton({
  showHeader = true,
  sectionCount = 1,     // number of section dividers to show
  fieldRows = 5,        // how many rows of fields (2 cols -> up to 10 cards)
  minHeight = 500,
}) {
  // Build an array of "rows"; each row has up to 2 field cards
  const rows = Array.from({ length: fieldRows });

  return (
    <Box sx={{ minHeight }}>
      {/* Form title + description */}
      {showHeader && (
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="text" width={240} height={28} />
          <Skeleton variant="text" width={360} height={18} />
        </Box>
      )}

      {/* Optional Section blocks that span full width */}
      {Array.from({ length: sectionCount }).map((_, i) => (
        <Paper key={`sec-${i}`} elevation={0} sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Skeleton variant="text" width={160} height={20} />
            <Stack direction="row" spacing={1}>
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="circular" width={28} height={28} />
            </Stack>
          </Stack>
          <Skeleton variant="text" width={220} height={22} />
          <Skeleton variant="text" width={360} height={16} />
          <Divider sx={{ mt: 1 }} />
        </Paper>
      ))}

      {/* Two-column grid of field “cards” */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        {rows.map((_, r) => (
          <React.Fragment key={`row-${r}`}>
            {/* Card 1 */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                gridColumn: 'span 1',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Skeleton variant="text" width={140} height={20} />
                <Stack direction="row" spacing={1}>
                  {/* mimic 1/2 col toggle + icons */}
                  <Skeleton variant="rectangular" width={76} height={28} />
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="circular" width={28} height={28} />
                </Stack>
              </Stack>
              <Skeleton variant="text" width={90} height={14} />
              <Skeleton variant="text" width={160} height={14} />
            </Paper>

            {/* Card 2 */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                gridColumn: 'span 1',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Skeleton variant="text" width={120} height={20} />
                <Stack direction="row" spacing={1}>
                  <Skeleton variant="rectangular" width={76} height={28} />
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="circular" width={28} height={28} />
                </Stack>
              </Stack>
              <Skeleton variant="text" width={110} height={14} />
              <Skeleton variant="text" width={180} height={14} />
            </Paper>
          </React.Fragment>
        ))}
      </Box>

      {/* Add Section button stub */}
      <Box sx={{ mt: 2 }}>
        <Skeleton variant="rectangular" width={140} height={36} />
      </Box>
    </Box>
  );
}